import { IntruderSnapshot } from '@/app/Alert/types';

/**
 * Crops an image based on percentage coordinates and returns the cropped image as a data URL
 * @param imageUrl - The URL of the image to crop
 * @param coords - The coordinates for cropping (in percentages)
 * @param quality - JPEG quality (0-1), defaults to 0.9
 * @returns Promise that resolves to the cropped image data URL, or original URL if cropping fails
 */
export async function cropImageToDataUrl(
  imageUrl: string,
  coords?: IntruderSnapshot,
  quality: number = 0.9
): Promise<string> {
  if (!coords?.leftPct || !coords?.topPct || !coords?.widthPct || !coords?.heightPct) {
    return imageUrl;
  }

  try {
    // Create an image element to load the source
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    });

    // Create a canvas for cropping
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return imageUrl;

    // Normalize and bound coordinates
    const normalizedLeftPct = Math.max(0, Math.min(100, coords.leftPct % 100));
    const normalizedTopPct = Math.max(0, Math.min(100, coords.topPct % 100));
    const normalizedWidthPct = Math.max(
      0,
      Math.min(100 - normalizedLeftPct, Math.min(coords.widthPct, 100))
    );
    const normalizedHeightPct = Math.max(
      0,
      Math.min(100 - normalizedTopPct, Math.min(coords.heightPct, 100))
    );

    // Calculate crop dimensions
    const cropX = Math.round((normalizedLeftPct / 100) * img.naturalWidth);
    const cropY = Math.round((normalizedTopPct / 100) * img.naturalHeight);
    const cropWidth = Math.round((normalizedWidthPct / 100) * img.naturalWidth);
    const cropHeight = Math.round((normalizedHeightPct / 100) * img.naturalHeight);

    if (cropWidth <= 0 || cropHeight <= 0) return imageUrl;

    // Set canvas size and draw
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    // Convert to base64
    const croppedDataUrl = canvas.toDataURL('image/jpeg', quality);
    return croppedDataUrl === 'data:,' || croppedDataUrl.length < 100 ? imageUrl : croppedDataUrl;
  } catch (error) {
    console.error('Error cropping image:', error);
    return imageUrl;
  }
}
